<template>
  <div class="service-locations-wrapper">
    <!-- Initial Action Buttons -->
    <div class="d-flex mb-3 gap-2">
      <button
        type="button"
        class="btn btn-outline-primary btn-sm mr-2"
        @click="toggleList"
      >
        {{ showList ? "Hide service locations" : "Show service locations" }}
      </button>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        @click="openCreateModal"
      >
        <i class="fas fa-plus mr-1"></i> Create service location
      </button>
    </div>

    <!-- Data Table View -->
    <div
      v-if="showList"
      class="table-responsive border rounded bg-light p-2 mb-3"
    >
      <div v-if="loading" class="text-center my-3">
        <span
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        ></span>
        Loading...
      </div>
      <div
        v-else-if="locations.length === 0"
        class="text-center my-3 text-muted"
      >
        No service locations defined for this project.
      </div>
      <table v-else class="table table-sm table-hover mb-0">
        <thead class="thead-light">
          <tr>
            <th>Port</th>
            <th>Country</th>
            <th>Effort %</th>
            <th>Note</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="loc in locations"
            :key="loc.id"
            :class="{ 'row-disabled': isMalta(loc) }"
            :style="isMalta(loc) ? 'cursor: not-allowed' : 'cursor: pointer'"
            @click="openEditModal(loc)"
          >
            <td>{{ loc.port }}</td>
            <td>{{ getCountryName(loc.country) }}</td>
            <td>{{ loc.effort_percentage }}%</td>
            <td class="text-truncate" style="max-width: 200px">
              {{ loc.note || "-" }}
            </td>
            <td class="text-right" @click.stop>
              <!-- Malta Core Balance Row: Show disabled badge -->
              <span v-if="isMalta(loc)" class="badge badge-secondary p-1">
                Fixed Balance
              </span>
              <!-- Standard Location Rows: Show delete button -->
              <button
                v-else
                type="button"
                class="btn btn-sm btn-outline-danger p-1 line-height-1"
                title="Delete Service Location"
                @click="deleteLocation(loc.id)"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Bootstrap Modal for Create / Update -->
    <div
      v-if="modal.show"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      style="background: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{
                modal.isEdit
                  ? "Update Service Location"
                  : "Create Service Location"
              }}
            </h5>
            <button type="button" class="close" @click="closeModal">
              <span>&times;</span>
            </button>
          </div>
          <form @submit.prevent="submitForm">
            <div class="modal-body">
              <!-- Port Field -->
              <div class="form-group">
                <label class="form-label"
                  >Port Name <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.port"
                  type="text"
                  class="form-control"
                  required
                  placeholder="e.g. Rotterdam"
                />
              </div>

              <!-- Country Select Dropdown (Malta Excluded) -->
              <div class="form-group">
                <label class="form-label"
                  >Country <span class="text-danger">*</span></label
                >
                <select v-model="form.country" class="form-control" required>
                  <option value="" disabled>Select a country</option>
                  <option
                    v-for="opt in filteredCountryOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.text }}
                  </option>
                </select>
              </div>

              <!-- Effort Percentage Field -->
              <div class="form-group">
                <label class="form-label"
                  >Percentage of Effort
                  <span class="text-danger">*</span></label
                >
                <div class="input-group">
                  <input
                    v-model.number="form.effort_percentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    class="form-control"
                    required
                    placeholder="e.g. 45.00"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">%</span>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div class="form-group">
                <label class="form-label">Note</label>
                <input
                  v-model="form.note"
                  type="text"
                  maxlength="255"
                  class="form-control"
                  placeholder="Optional brief notes..."
                />
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeModal"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="submitting"
              >
                {{
                  submitting
                    ? "Saving..."
                    : modal.isEdit
                    ? "Update Service Location"
                    : "Create Service Location"
                }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CountryOptions } from "@/constants/address";
import {
  getProjectServiceLocations,
  createProjectServiceLocation,
  updateProjectServiceLocation,
  deleteProjectServiceLocation,
} from "@/apis/projects";
import { notifyError, notifySuccess } from "@/helpers/notification";

export default {
  name: "ProjectServiceLocations",
  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      locations: [],
      showList: false,
      loading: false,
      submitting: false,
      countryOptions: CountryOptions,
      modal: {
        show: false,
        isEdit: false,
        selectedId: null,
      },
      form: {
        port: "",
        country: "",
        effort_percentage: null,
        note: "",
        project_id: null,
      },
    };
  },
  computed: {
    // Exclude Malta (MT) from selection options
    filteredCountryOptions() {
      return this.countryOptions.filter(
        (opt) => opt.value?.toUpperCase() !== "MT"
      );
    },
  },
  methods: {
    isMalta(location) {
      return location?.country?.toUpperCase() === "MT";
    },

    getCountryName(code) {
      const option = this.countryOptions.find((o) => o.value === code);
      return option ? option.text : code;
    },

    getErrorMessage(err) {
      const responseData = err?.response?.data;
      if (!responseData) return err?.message || "An unexpected error occurred.";
      if (typeof responseData === "string") return responseData;
      return (
        responseData.message ||
        responseData.body ||
        JSON.stringify(responseData)
      );
    },

    async toggleList() {
      this.showList = !this.showList;
      if (this.showList) {
        await this.fetchLocations();
      }
    },

    async fetchLocations() {
      if (!this.projectId) return;
      this.loading = true;
      try {
        const response = await getProjectServiceLocations(this.projectId);
        this.locations = response.data;
      } catch (err) {
        console.error("Failed fetching service locations", err);
        notifyError(this, this.getErrorMessage(err));
      } finally {
        this.loading = false;
      }
    },

    openCreateModal() {
      this.modal.isEdit = false;
      this.modal.selectedId = null;
      this.form = {
        port: "",
        country: "",
        effort_percentage: null,
        note: "",
        project_id: this.projectId,
      };
      this.modal.show = true;
    },

    openEditModal(location) {
      // Prevent opening edit modal for Malta rows
      if (this.isMalta(location)) {
        return;
      }

      this.modal.isEdit = true;
      this.modal.selectedId = location.id;
      this.form = {
        port: location.port,
        country: location.country,
        effort_percentage: location.effort_percentage,
        note: location.note || "",
        project_id: this.projectId,
      };
      this.modal.show = true;
    },

    closeModal() {
      this.modal.show = false;
    },

    async submitForm() {
      this.submitting = true;
      this.form.project_id = this.projectId;
      try {
        if (this.modal.isEdit) {
          this.form.id = this.modal.selectedId;
          await updateProjectServiceLocation(this.form);
          notifySuccess(this, "Service location updated successfully.");
        } else {
          await createProjectServiceLocation(this.form);
          notifySuccess(this, "Service location created successfully.");
        }
        this.closeModal();
        await this.fetchLocations();
        this.showList = true;
      } catch (err) {
        console.error("Error saving service location record", err);
        notifyError(this, this.getErrorMessage(err));
      } finally {
        this.submitting = false;
      }
    },

    async deleteLocation(locationId) {
      if (!confirm("Are you sure you want to delete this service location?"))
        return;
      try {
        await deleteProjectServiceLocation(locationId, this.projectId);
        notifySuccess(this, "Service location deleted successfully.");
        await this.fetchLocations();
      } catch (err) {
        console.error("Error removing service location record", err);
        notifyError(this, this.getErrorMessage(err));
      }
    },
  },
};
</script>

<style scoped>
.line-height-1 {
  line-height: 1;
}
.gap-2 {
  gap: 0.5rem;
}
.table-responsive {
  max-height: 300px;
  overflow-y: auto;
}

/* Darker gray background style for the non-editable Malta row */
.row-disabled {
  background-color: #e9ecef !important;
  color: #6c757d;
}
.row-disabled:hover {
  background-color: #e2e6ea !important;
}
</style>
